import { __assign } from "tslib";
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { BiDataHook, ReferralType } from '@wix/da-bi/pkg/constants';
import { SubscriptionPurchaseFTUEContext } from '../../../Tier/ftue/SubscriptionPurchaseFTUE/SubscriptionPurchaseFTUEContext';
import { useIsCurrentUserDeviationAuthor } from '../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import ActionBarItemWrapper from '../../_partials/items/ActionBarItemWrapper';
import SubscribeToUnlockTooltip from '../../_partials/SubscribeToUnlockTooltip';
import EditPcpLink from './EditPcpLink';
import DownloadButtonInner from './DownloadButtonInner';
import DownloadPopover from './DownloadPopover';
/** There are two types of download available for deviations: free and premium
 *  and the deviation can have one/both/none - all controled through this button. */
var DownloadButton = function (_a) {
    var _b, _c;
    var deviation = _a.deviation, onDownload = _a.onDownload, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave;
    var t = useTranslation().t;
    var _d = useRestrictToLoggedIn(), isLoggedIn = _d.isLoggedIn, restrictToLoggedIn = _d.restrictToLoggedIn;
    var tierAccess = deviation.tierAccess;
    var isOwner = useIsCurrentUserDeviationAuthor(deviation);
    // when the user first purchases a subscription, open the tooltip and highlight the icon
    var subscriptionPurchaseFTUEPhase = useContext(SubscriptionPurchaseFTUEContext);
    var isButtonHighlighted = subscriptionPurchaseFTUEPhase === 'tooltips';
    // when downloaded, open a tooltip to suggest making a comment too
    var renderDownloadPopover = useCallback(function (_a) {
        var closeTooltip = _a.closeTooltip;
        return (React.createElement(DownloadPopover, { deviation: deviation, closeTooltip: closeTooltip, onDownload: onDownload, excludeFreeDownload: tierAccess === 'locked-subscribed' }));
    }, [deviation, onDownload, tierAccess]);
    var premiumDownload = (_b = deviation.extended) === null || _b === void 0 ? void 0 : _b.pcp;
    var freeDownload = (_c = deviation.extended) === null || _c === void 0 ? void 0 : _c.download;
    var hasNeitherDownload = !premiumDownload && !freeDownload;
    if (hasNeitherDownload) {
        return null;
    }
    if (isOwner) {
        return React.createElement(EditPcpLink, { deviation: deviation });
    }
    if (!isLoggedIn) {
        // must be logged in for both free and premium downloads
        var tooltip_1 = t('actionbar.download.mustLogIn');
        return (React.createElement(ActionBarItemWrapper, { tooltip: tooltip_1, forceVisibleTooltip: isButtonHighlighted },
            React.createElement(DownloadButtonInner, { tooltip: tooltip_1, deviationId: deviation.deviationId, isPaidDownload: !!premiumDownload, isHighlighted: isButtonHighlighted, showPremiumIcon: !!premiumDownload || tierAccess === 'locked', onClick: restrictToLoggedIn(ReferralType.ANON_DOWNLOAD) })));
    }
    // must have a subscription for in-tier free downloads
    var hasFreeOnly = !premiumDownload && !!freeDownload;
    var isBlockedBySubscription = tierAccess === 'locked' || tierAccess === 'locked-subscribed';
    if (hasFreeOnly && isBlockedBySubscription) {
        return (React.createElement(SubscribeToUnlockTooltip, { deviation: deviation, forceVisible: isButtonHighlighted },
            React.createElement(DownloadButtonInner, { deviationId: deviation.deviationId, isHighlighted: isButtonHighlighted })));
    }
    var _e = getButtonProps(premiumDownload, freeDownload, tierAccess), tooltipKey = _e.tooltipKey, opensPopover = _e.opensPopover, downloadProps = _e.downloadProps;
    var tooltip = t(tooltipKey);
    return (React.createElement(ActionBarItemWrapper, { tooltip: tooltip, forceVisibleTooltip: isButtonHighlighted, renderPopover: opensPopover ? renderDownloadPopover : undefined },
        React.createElement(DownloadButtonInner, __assign({}, downloadProps, { deviationId: deviation.deviationId, isHighlighted: isButtonHighlighted, "aria-label": tooltip, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onClick: downloadProps.downloadUrl ? onDownload : undefined }))));
};
DownloadButton.displayName = 'DownloadButton';
export default DownloadButton;
// just to isolate some of the convoluted access logic
function getButtonProps(premiumDownload, freeDownload, tierAccess) {
    var hasFreeOnly = !premiumDownload && !!freeDownload;
    var hasPremiumOnly = !!premiumDownload && !freeDownload;
    var hasBoth = !!premiumDownload && !!freeDownload;
    var purchasedSeparately = premiumDownload === null || premiumDownload === void 0 ? void 0 : premiumDownload.hasUserPurchased;
    var hasSubscriptionAccess = tierAccess === 'unlocked';
    var hasPremiumAccess = purchasedSeparately || hasSubscriptionAccess;
    var needsToBuyOrSubscribe = !purchasedSeparately && tierAccess === 'locked';
    var hasBothButFreeIsBlocked = hasBoth && tierAccess === 'locked-subscribed';
    // used for both tooltip and button aria-label
    var tooltipKey = 'actionbar.download';
    // does the button open the download popover?
    var opensPopover = false;
    var downloadProps = {};
    // must have a subscription for in-tier free downloads
    if (hasFreeOnly) {
        downloadProps = __assign(__assign({}, downloadProps), { downloadUrl: freeDownload === null || freeDownload === void 0 ? void 0 : freeDownload.url, dataHook: BiDataHook.DEVIATION_DOWNLOAD_BUTTON });
    }
    else if ((hasPremiumOnly && hasPremiumAccess) ||
        (hasBothButFreeIsBlocked && purchasedSeparately)) {
        // already purchased or subscribed premium download
        downloadProps = __assign(__assign({}, downloadProps), { downloadUrl: premiumDownload === null || premiumDownload === void 0 ? void 0 : premiumDownload.url, isPaidDownload: true });
    }
    else {
        // both free and pcp, or pcp only and needs purchase or subscription
        tooltipKey =
            hasPremiumOnly || hasBothButFreeIsBlocked
                ? 'actionbar.pcp'
                : needsToBuyOrSubscribe
                    ? 'actionbar.pcp.buyOrSubscribe'
                    : 'actionbar.pcp.premiumAndFree';
        opensPopover = true;
        downloadProps = __assign(__assign({}, downloadProps), { isPaidDownload: true, showPremiumIcon: !purchasedSeparately && !hasSubscriptionAccess });
    }
    return {
        tooltipKey: tooltipKey,
        opensPopover: opensPopover,
        downloadProps: downloadProps,
    };
}
//# sourceMappingURL=DownloadButton.js.map